import { useEffect, useState, useRef } from "react";
import { useSession } from "Context/userAuthContext";
import { useNavigate } from "react-router-dom";
import { publishProfile, uploadImage } from "Util/firebaseHelper";
import _ from "lodash";
import {
  Draggable,
  DragDropContext,
  Droppable,
  DropResult,
} from "react-beautiful-dnd";
import { reorder, compressImage } from "Util/helpers";
import { BusinessInfo } from "Model/BusinessInfo";
import { readBusinessInfo } from "Util/firebaseHelper";
import { SITE_URL } from "Constants";

// ui related
import Stories from "react-insta-stories";
import { Chip, Icon, ThemeProvider } from "@mui/material";
import { createCustomTheme } from "Components/Styles/Styles";
import {
  como,
  ferra,
  eclipse,
  black,
  sorbus,
  darkPink,
  denim,
  blueViolet,
  contessa,
  royalHeath,
  danube,
  deepLilac,
  mint,
  green,
  lime,
} from "Components/Styles/colors";
import {
  Button,
  Box,
  Typography,
  Stack,
  List,
  ListItemText,
  ListItemIcon,
  ListItemButton,
  Divider,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  TextField,
  Link,
  Popover,
  IconButton,
} from "@mui/material";
import {
  AddExternalLink,
  CardStyle,
  InfoBox,
  ProfileSettings,
} from "Components/AllComponents";
import { Profile } from "Model/Profile";
import { useAppDispatch } from "Redux/hooks";
import { editSellerProfile } from "Redux/actionCreators";
import { AvailabilityType } from "Model/Availability";
import "react-responsive-carousel/lib/styles/carousel.min.css";
import "Components/Styles/carousel.css";
import { PresaleEntry } from "Pages/CustomerView/CustomerPresale/Components/PresaleEntry";
import { toast } from "react-toastify";
import { ManageGallery } from "Components/BusinessView/ManageGallery";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import InsertPhotoOutlinedIcon from "@mui/icons-material/InsertPhotoOutlined";
import { LoadingButton } from "@mui/lab";

// icons
import EditIcon from "@mui/icons-material/Edit";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { EditPaymentLinks } from "Pages/BusinessView/Hub/Home/Components/EditPaymentLinks";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import CircleIcon from "@mui/icons-material/Circle";
import FormatColorFillRoundedIcon from "@mui/icons-material/FormatColorFillRounded";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import RocketLaunchOutlinedIcon from "@mui/icons-material/RocketLaunchOutlined";

// ============================================================================
// HOOK TO DETECT TRUNCATION
// ============================================================================

const useIsTruncated = (text: string, isExpanded: boolean) => {
  const [isTruncated, setIsTruncated] = useState(false);
  const textRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const checkTruncation = () => {
      if (textRef.current && !isExpanded) {
        const element = textRef.current;
        // Check if content would be truncated when collapsed
        setIsTruncated(element.scrollHeight > element.clientHeight);
      }
    };

    checkTruncation();
    window.addEventListener("resize", checkTruncation);
    return () => window.removeEventListener("resize", checkTruncation);
  }, [text, isExpanded]);

  return { isTruncated, textRef };
};

// ============================================================================
// EXTRACTED COMPONENTS
// ============================================================================

const SellerAvatar = ({ photo }: { photo: string }) => (
  <Stack
    sx={{
      backgroundColor: "white",
      p: 0.25,
      borderRadius: "50%",
      alignItems: "center",
      textAlign: "center",
      width: "fit-content",
      zIndex: "1",
    }}
  >
    {photo === "" ? (
      <svg
        viewBox="0 0 36 36"
        fill="none"
        role="img"
        xmlns="http://www.w3.org/2000/svg"
        width="64"
        height="64"
      >
        <mask
          id=":r126:"
          maskUnits="userSpaceOnUse"
          x="0"
          y="0"
          width="36"
          height="36"
        >
          <rect width="36" height="36" rx="72" fill="#FFFFFF" />
        </mask>
        <g mask="url(#:r126:)">
          <rect width="36" height="36" fill="#c8c8b9" />
          <rect
            x="0"
            y="0"
            width="36"
            height="36"
            transform="translate(-2 6) rotate(306 18 18) scale(1)"
            fill="#eeda8d"
            rx="36"
          />
          <g transform="translate(-2 6) rotate(6 18 18)">
            <path d="M13,19 a1,0.75 0 0,0 10,0" fill="#000000" />
            <rect
              x="13"
              y="14"
              width="1.5"
              height="2"
              rx="1"
              stroke="none"
              fill="#000000"
            />
            <rect
              x="21"
              y="14"
              width="1.5"
              height="2"
              rx="1"
              stroke="none"
              fill="#000000"
            />
          </g>
        </g>
      </svg>
    ) : (
      <img
        src={photo}
        width={64}
        height={64}
        style={{ objectFit: "cover", borderRadius: "50%" }}
      />
    )}
  </Stack>
);

const SellerHeader = ({
  sellerProfile,
  openBusinessProfileModal,
  openBusinessProfile,
  closeBusinessProfileModal,
}: {
  sellerProfile: Profile;
  openBusinessProfileModal: () => void;
  openBusinessProfile: boolean;
  closeBusinessProfileModal: () => void;
}) => (
  <Stack
    flex={1}
    direction="row"
    alignItems="center"
    justifyContent="space-between"
  >
    <Stack flex={1} spacing={0.5}>
      <Typography
        variant="h5"
        onClick={(e) => {
          e.stopPropagation();
          openBusinessProfileModal();
        }}
        sx={{
          textDecoration: "underline",
          textUnderlineOffset: 2,
          "&:hover": { color: "primary.main" },
        }}
      >
        {sellerProfile.businessName}
      </Typography>
      <Box sx={{ display: "block" }}>
        <Chip
          label={`${sellerProfile.sellerCity || "City"}, ${sellerProfile.sellerState || "State"}`}
          size="small"
          sx={{ mr: 1, mt: 0.5 }}
        />
        <Chip
          label={sellerProfile.fulfillmentMethod}
          size="small"
          sx={{ mt: 0.5 }}
        />
      </Box>
    </Stack>
    <SellerAvatar photo={sellerProfile.sellerPhoto} />
    <ProfileSettings
      autoPop={false}
      isModalOpen={openBusinessProfile}
      openModal={openBusinessProfileModal}
      closeModal={closeBusinessProfileModal}
    />
  </Stack>
);

const SellerBio = ({
  sellerProfile,
  isBioExpanded,
  textRef,
}: {
  sellerProfile: Profile;
  isBioExpanded: boolean;
  textRef: React.RefObject<HTMLDivElement>;
}) => (
  <Stack>
    {sellerProfile.sellerName && (
      <Typography variant="subtitle2">
        Meet {sellerProfile.sellerName}
      </Typography>
    )}
    {sellerProfile.sellerIntro && (
      <Typography
        ref={textRef}
        variant="body1"
        sx={{
          color: "text.primary",
          whiteSpace: "pre-line",
          ...(!isBioExpanded && {
            display: "-webkit-box",
            WebkitLineClamp: { xs: 1, md: 2 },
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }),
        }}
      >
        {sellerProfile.sellerIntro}
      </Typography>
    )}
  </Stack>
);

const ColorMenuItem = ({
  color,
  colorName,
  onClick,
}: {
  color: any;
  colorName: string;
  onClick: () => void;
}) => (
  <IconButton
    size="large"
    sx={{ color: color[700], padding: 1 }}
    onClick={onClick}
  >
    <CircleIcon fontSize="inherit" />
  </IconButton>
);

type UserLinkProps = {
  title: string;
  url: string;
  id: string;
  index: number;
};

const UserLink = (props: UserLinkProps) => {
  const { user, sellerProfile } = useSession();
  const [oldTitle, setOldTitle] = useState(props.title);
  const [title, setTitle] = useState(props.title);
  const [oldUrl, setOldUrl] = useState(props.url);
  const [url, setUrl] = useState(props.url);
  const [openModal, setOpenModal] = useState(false);
  const dispatch = useAppDispatch();

  const handleOpenModal = () => setOpenModal(true);
  const handleCloseModal = () => {
    setOpenModal(false);
    setTitle(oldTitle);
    setUrl(oldUrl);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    let tempLinks = _.cloneDeep(sellerProfile.links);
    tempLinks[props.index].title = title;
    tempLinks[props.index].url = url;
    publishProfile({ ...sellerProfile, links: tempLinks }, user?.uid || "");
    dispatch(editSellerProfile({ ...sellerProfile, links: tempLinks }));
    setOpenModal(false);
    setOldTitle(title);
    setOldUrl(url);
  };

  const handleDelete = () => {
    let tempLinks = _.cloneDeep(sellerProfile.links);
    tempLinks.splice(props.index, 1);
    publishProfile({ ...sellerProfile, links: tempLinks }, user?.uid || "");
    dispatch(editSellerProfile({ ...sellerProfile, links: tempLinks }));
    setOpenModal(false);
  };

  const handleUrlBlur = () => {
    if (url.indexOf("http://") !== 0 && url.indexOf("https://") !== 0) {
      setUrl("http://" + url);
    }
  };

  return (
    <Draggable draggableId={props.id} index={props.index}>
      {(provided) => (
        <div>
          <ListItemButton
            ref={provided.innerRef}
            {...provided.draggableProps}
            {...provided.dragHandleProps}
            onClick={handleOpenModal}
          >
            <ListItemIcon>
              <DragIndicatorIcon />
            </ListItemIcon>
            <ListItemText
              primary={<Typography variant="subtitle2">{oldTitle}</Typography>}
            />
            <EditIcon sx={{ ml: 2 }} />
          </ListItemButton>
          <Dialog open={openModal} onClose={handleCloseModal} fullWidth>
            <DialogTitle>Edit link</DialogTitle>
            <form onSubmit={handleSubmit}>
              <DialogContent>
                <TextField
                  fullWidth
                  required
                  id="title"
                  label="Title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                />
                <TextField
                  fullWidth
                  margin="normal"
                  required
                  id="url"
                  label="Url"
                  type="url"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  onBlur={handleUrlBlur}
                />
                <Button
                  startIcon={<DeleteRoundedIcon />}
                  variant="text"
                  onClick={handleDelete}
                  sx={{ width: "fit-content", color: "text.secondary", mt: 1 }}
                >
                  Delete link
                </Button>
              </DialogContent>
              <DialogActions>
                {user && (
                  <div>
                    <Button onClick={handleCloseModal} color="secondary">
                      Cancel
                    </Button>
                    <Button type="submit">Save</Button>
                  </div>
                )}
              </DialogActions>
            </form>
          </Dialog>
        </div>
      )}
    </Draggable>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

type HomeProps = {
  sellerProfile: Profile;
};

export const HomeContent = ({ sellerProfile }: HomeProps) => {
  const { user } = useSession();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [businessInfo, setBusinessInfo] = useState<BusinessInfo>();
  const formPublished = sellerProfile.forms && sellerProfile.forms.length > 0;

  const [localLinks, setLocalLinks] = useState(sellerProfile.links);
  const [uploadingPortrait, setUploadingPortrait] = useState(false);
  const [anchorColorMenu, setAnchorColorMenu] = useState<null | HTMLElement>(
    null
  );
  const [isBioExpanded, setIsBioExpanded] = useState(false);
  const { isTruncated, textRef } = useIsTruncated(
    sellerProfile.sellerIntro || "",
    isBioExpanded
  );

  const handleOpenColorMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorColorMenu(event.currentTarget);
  };
  const handleCloseColorMenu = () => setAnchorColorMenu(null);
  const toggleExpand = () => setIsBioExpanded(!isBioExpanded);

  const handlePortraitUpload = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setUploadingPortrait(true);
    if (user && e.target.files && e.target.files.length > 0) {
      const image = e.target.files[0];
      const resizedImage = await compressImage(image, 800, 1000);
      const url = await uploadImage(
        resizedImage as File,
        `${user.uid}_portrait`
      );
      const updatedProfile = { ...sellerProfile, sellerPortrait: url };
      await publishProfile(updatedProfile, user.uid);
      dispatch(editSellerProfile(updatedProfile));
      console.log("uploaded portrait", url, "Size:", resizedImage.size);
    }
    setUploadingPortrait(false);
  };

  const handleDeletePortrait = async () => {
    if (user) {
      const updatedProfile = { ...sellerProfile, sellerPortrait: "" };
      await publishProfile(updatedProfile, user.uid);
      dispatch(editSellerProfile(updatedProfile));
      console.log("deleted portrait");
    }
  };

  const updateProfile = (updates: Partial<Profile>) => {
    const updatedProfile = { ...sellerProfile, ...updates };
    publishProfile(updatedProfile, user?.uid || "");
    dispatch(editSellerProfile(updatedProfile));
  };

  const onDragEnd = ({ destination, source }: DropResult) => {
    if (!destination) return;
    const newLinks = reorder(localLinks, source.index, destination.index);
    setLocalLinks(newLinks);
    updateProfile({ links: newLinks });
  };

  const onColorChanged = (color: string) => {
    updateProfile({ colorPalette: color });
  };

  const copyLinkToClipboard = () => {
    navigator.clipboard.writeText(`${SITE_URL}/${sellerProfile.username}`);
    toast("Link copied.", { position: "top-center", autoClose: 800 });
  };

  useEffect(() => {
    setLocalLinks(sellerProfile.links);
  }, [sellerProfile.links]);

  useEffect(() => {
    async function fetchData() {
      if (formPublished) {
        const form = await readBusinessInfo(sellerProfile.forms[0]);
        let content = form.data();
        if (content) {
          setBusinessInfo({
            id: content.id,
            businessName: content.businessName,
            outsideUS: content.outsideUS || false,
            formIntro: content.formIntro,
            sellerName: content.sellerName,
            sellerIntro: content.sellerIntro,
            sellerCity: content.sellerCity,
            sellerState: content.sellerState,
            shipOnlyToMyState: content.shipOnlyToMyState,
            fulfillmentMethod: content.fulfillmentMethod,
            minOrder: content.minOrder,
            priceChoice: content.priceChoice,
            price: content.price,
            sellerBookedUp: content.sellerBookedUp,
            sellerAvailability: content.sellerAvailability,
            orderDetails: content.orderDetails,
            sellerEmail: content.sellerEmail,
            bucketLists: content.bucketLists || [""],
            flavors: content.flavors || [""],
            packagings: content.packagings || [""],
            paymentTypes: content.paymentTypes || [""],
            agreements: content.agreements || "",
            showBucketLists: content.showBucketLists || false,
            showFlavors: content.showFlavors || false,
            showPackagings: content.showPackagings || false,
            showPaymentTypes: content.showPaymentTypes || false,
            sellerPhoto: content.sellerPhoto || "",
            aic: content.aic || false,
            username: content.username || "",
          });
        }
      }
    }
    fetchData();
  }, [formPublished, sellerProfile.forms]);

  const colorOptions = [
    { color: black, name: "black" },
    { color: eclipse, name: "eclipse" },
    { color: ferra, name: "ferra" },
    { color: contessa, name: "contessa" },
    { color: sorbus, name: "sorbus" },
    { color: lime, name: "lime" },
    { color: green, name: "green" },
    { color: como, name: "como" },
    { color: mint, name: "mint" },
    { color: danube, name: "danube" },
    { color: denim, name: "denim" },
    { color: blueViolet, name: "blueViolet" },
    { color: deepLilac, name: "deepLilac" },
    { color: royalHeath, name: "royalHeath" },
    { color: darkPink, name: "darkPink" },
  ];

  const [openBusinessProfile, setOpenBusinessProfile] = useState(false);
  const openBusinessProfileModal = () => {
    setOpenBusinessProfile(true);
  };
  const closeBusinessProfileModal = () => {
    setOpenBusinessProfile(false);
  };

  return (
    <ThemeProvider theme={createCustomTheme(sellerProfile.colorPalette)}>
      <Stack
        spacing={{ xs: 2, md: 3 }}
        sx={{
          backgroundColor: "background.default",
          p: { xs: 2, md: 3 },
          border: { xs: "none", md: "1.5px solid #14192d" },
          outline: { xs: "1.5px solid #14192d", md: "none" },
          borderRadius: 6,
        }}
      >
        {/* Header with link and color picker */}
        <Stack direction="row" alignItems="center" spacing={1}>
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            flex={1}
            sx={{
              py: 1,
              px: 2,
              backgroundColor: "white",
              borderRadius: 8,
              border: "1px solid",
              borderColor: "grey.200",
              minWidth: 0,
            }}
          >
            <Link
              flex={1}
              variant="subtitle2"
              href={"/" + sellerProfile.username}
              target="_blank"
              noWrap={false}
              sx={{
                wordBreak: "break-all",
                minWidth: 0,
                color: "text.primary",
              }}
            >
              {`${SITE_URL}/${sellerProfile.username}`}
            </Link>
            <IconButton
              size="small"
              onClick={copyLinkToClipboard}
              sx={{ flexShrink: 0 }}
            >
              <ContentCopyIcon />
            </IconButton>
          </Stack>
          <>
            <IconButton
              size="small"
              color="primary"
              onClick={handleOpenColorMenu}
            >
              <FormatColorFillRoundedIcon />
            </IconButton>
            <Popover
              id="menu-colors"
              anchorEl={anchorColorMenu}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
              open={Boolean(anchorColorMenu)}
              onClose={handleCloseColorMenu}
            >
              <Stack sx={{ p: 1 }}>
                {[0, 4, 8, 12, 16, 20].map((startIdx) => (
                  <Box key={startIdx}>
                    {colorOptions.slice(startIdx, startIdx + 4).map((opt) => (
                      <ColorMenuItem
                        key={opt.name}
                        color={opt.color}
                        colorName={opt.name}
                        onClick={() => onColorChanged(opt.name)}
                      />
                    ))}
                  </Box>
                ))}
              </Stack>
            </Popover>
          </>
        </Stack>

        {/* Profile section */}
        {sellerProfile.sellerName || sellerProfile.sellerIntro ? (
          <Box
            sx={{
              position: "relative",
              width: "100%",
              aspectRatio: sellerProfile.sellerPortrait
                ? "4/3"
                : { xs: "5/4", sm: "2/1" },
              overflow: "hidden",
              borderRadius: 5,
            }}
          >
            {sellerProfile.sellerPortrait ? (
              <>
                {!isBioExpanded && (
                  <IconButton
                    aria-label="delete"
                    size="small"
                    color="secondary"
                    onClick={handleDeletePortrait}
                    sx={{
                      position: "absolute",
                      top: 8,
                      right: 8,
                      zIndex: 2,
                      background: "rgba(255,255,255,1)",
                      "&:hover": {
                        backgroundColor: "rgba(255,255,255,0.8)",
                      },
                    }}
                  >
                    <DeleteOutlineRoundedIcon />
                  </IconButton>
                )}
                <LoadingButton
                  disabled={isBioExpanded}
                  loading={uploadingPortrait}
                  color="secondary"
                  component="label"
                  sx={{
                    width: "100%",
                    height: "100%",
                    p: 0,
                  }}
                >
                  <img
                    src={sellerProfile.sellerPortrait}
                    alt={sellerProfile.sellerName}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      transition: "all 0.1s ease",
                      opacity: isBioExpanded ? 0.8 : 1,
                    }}
                  />
                  <input
                    hidden
                    accept="image/*"
                    type="file"
                    onChange={handlePortraitUpload}
                  />
                </LoadingButton>
              </>
            ) : (
              <LoadingButton
                disabled={isBioExpanded}
                loading={uploadingPortrait}
                color="secondary"
                component="label"
                sx={{
                  width: "100%",
                  height: "100%",
                  p: 0,
                  backgroundColor: isBioExpanded
                    ? "rgba(0,0,0,0.2)"
                    : "rgba(0,0,0,0.05)",
                }}
              >
                <InsertPhotoOutlinedIcon
                  fontSize="large"
                  sx={{
                    opacity: 0.5,
                    transform: {
                      xs: "translateY(-210%)",
                      sm: "translateY(-275%)",
                    },
                  }}
                />
                <input
                  hidden
                  accept="image/*"
                  type="file"
                  onChange={handlePortraitUpload}
                />
              </LoadingButton>
            )}

            {/* Text Content Overlay */}
            <Stack
              spacing={1}
              sx={{
                cursor: isTruncated || isBioExpanded ? "pointer" : "default",
                maxHeight: "100%",
                overflowY: isBioExpanded ? "auto" : "visible",
                overflowX: "hidden",
                padding: { xs: 2, sm: 3 },
                display: "flex",
                flexDirection: "column",
                backgroundColor: "white",
                scrollbarWidth: "none",
                "&::-webkit-scrollbar": {
                  display: "none",
                },
                position: "absolute",
                bottom: 0,
                left: 0,
                right: 0,
                zIndex: 1,
                transition: "all 0.3s ease",
              }}
              onClick={(e) => {
                if (openBusinessProfile) return;
                if (isTruncated || isBioExpanded) {
                  e.stopPropagation();
                  toggleExpand();
                }
              }}
            >
              <SellerHeader
                sellerProfile={sellerProfile}
                openBusinessProfileModal={openBusinessProfileModal}
                openBusinessProfile={openBusinessProfile}
                closeBusinessProfileModal={closeBusinessProfileModal}
              />
              <SellerBio
                sellerProfile={sellerProfile}
                isBioExpanded={isBioExpanded}
                textRef={textRef}
              />
            </Stack>
          </Box>
        ) : (
          <Stack spacing={1}>
            <SellerHeader
              sellerProfile={sellerProfile}
              openBusinessProfileModal={openBusinessProfileModal}
              openBusinessProfile={openBusinessProfile}
              closeBusinessProfileModal={closeBusinessProfileModal}
            />
            <SellerBio
              sellerProfile={sellerProfile}
              isBioExpanded={false}
              textRef={textRef}
            />
          </Stack>
        )}

        {/* Onboarding prompts */}
        {!formPublished ||
        sellerProfile.presales.length === 0 ||
        sellerProfile.payments.length === 0 ? (
          <CardStyle
            sx={{
              backgroundColor: "transparent",
              border: "1px dashed",
              borderColor: "primary.light",
            }}
          >
            <Stack spacing={2}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <RocketLaunchOutlinedIcon />
                <Typography variant="h6">Finish your setup</Typography>
              </Stack>
              {sellerProfile.businessName === "" ? (
                <InfoBox sx={{ backgroundColor: "white" }}>
                  <Typography color="text.primary" variant="h6">
                    Complete your profile
                  </Typography>
                  <Typography color="text.secondary" mt={0.5} mb={1}>
                    Tell your customer about you and your business.
                  </Typography>
                  <Button
                    variant="outlined"
                    onClick={(e) => {
                      e.stopPropagation();
                      openBusinessProfileModal();
                    }}
                    sx={{ width: "fit-content" }}
                  >
                    Edit profile
                  </Button>
                </InfoBox>
              ) : null}
              {sellerProfile.payments.length === 0 && (
                <InfoBox sx={{ backgroundColor: "white" }}>
                  <Typography color="text.primary" variant="h6">
                    Add payments
                  </Typography>
                  <Typography color="text.secondary" mt={0.5} mb={1}>
                    Add your payment methods and link your Square account to
                    enable direct checkout for presales.
                  </Typography>
                  <EditPaymentLinks empty />
                </InfoBox>
              )}
              {!formPublished && (
                <InfoBox sx={{ backgroundColor: "white" }}>
                  <Typography color="text.primary" variant="h6">
                    Take custom orders
                  </Typography>
                  <Typography color="text.secondary" mt={0.5} mb={1}>
                    Build an order form in under a minute.
                  </Typography>
                  <Button
                    variant="outlined"
                    onClick={() => navigate("/admin/form")}
                    sx={{ width: "fit-content" }}
                  >
                    Use template
                  </Button>
                </InfoBox>
              )}
              {sellerProfile.presales.length === 0 && (
                <InfoBox sx={{ backgroundColor: "white" }}>
                  <Typography color="text.primary" variant="h6">
                    Launch a presale
                  </Typography>
                  <Typography color="text.secondary" mt={0.5} mb={1}>
                    Start selling instantly by adding your items.
                  </Typography>
                  <Button
                    variant="outlined"
                    onClick={() => navigate("/admin/presales")}
                    sx={{ width: "fit-content" }}
                  >
                    Create presale
                  </Button>
                </InfoBox>
              )}
            </Stack>
          </CardStyle>
        ) : null}

        {/* Presales and order sections */}
        {sellerProfile.livePresales.map((p) => (
          <PresaleEntry
            editing={true}
            key={p}
            presaleId={p}
            username={sellerProfile.username}
          />
        ))}

        {/* Custom order */}
        {formPublished && (
          <CardStyle
            sx={{
              borderRadius: 5,
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography
                variant="h6"
                flex={1}
                sx={{
                  fontFamily: "Lora",
                  fontSize: "1.3rem",
                }}
              >
                Custom order
              </Typography>
              <Link
                onClick={() => navigate("/admin/form")}
                variant="button"
                color="text.primary"
                sx={{
                  textDecoration: "none",
                  textTransform: "none",
                  "&:hover": { color: "text.secondary" },
                }}
              >
                Edit form
              </Link>
            </Stack>
            <Stack>
              {sellerProfile.availability.type ===
              AvailabilityType.doNotAcceptOrder ? (
                <Typography flex={1} variant="body1" color="text.secondary">
                  Not accepting orders
                </Typography>
              ) : (
                <Typography variant="body1" color="text.secondary">
                  Starts at ${businessInfo?.price}/dozen •{" "}
                  {businessInfo?.minOrder} dozen minimum
                </Typography>
              )}
            </Stack>
            <ManageGallery>
              {!sellerProfile.hideImageGallery &&
              sellerProfile.imageGallery.length > 0 ? (
                <Stack sx={{ marginTop: 2 }}>
                  <Stories
                    key={sellerProfile.imageGallery.join(",")} // Force remount on change
                    preventDefault
                    stories={sellerProfile.imageGallery.map((url) => ({
                      url,
                    }))}
                    defaultInterval={2000}
                    width="100%"
                    height="100%"
                    loop
                    storyStyles={{ width: "100%", overflow: "hidden" }}
                    storyContainerStyles={{
                      borderRadius: 12,
                      overflow: "hidden",
                      aspectRatio: "1/1",
                    }}
                    storyInnerContainerStyles={{
                      backgroundColor: "white",
                      justifyContent: "center",
                      overflow: "hidden",
                    }}
                    progressContainerStyles={{
                      filter: "none",
                      width: "200px",
                      bottom: 0,
                    }}
                  />
                </Stack>
              ) : (
                <Button variant="outlined" sx={{ mt: 1, width: "100%" }}>
                  Add gallery
                </Button>
              )}
            </ManageGallery>
          </CardStyle>
        )}

        {/* Email list */}
        {!sellerProfile.disableCustomerEmailList && (
          <CardStyle onClick={() => navigate("/admin/customers")}>
            <Stack>
              <Typography
                variant="h6"
                flex={1}
                sx={{ fontFamily: "Lora", fontSize: "1.3rem" }}
              >
                Never miss a presale
              </Typography>
              <Typography variant="body1">
                Join the email list to be the first to know when a new presale
                launches.
              </Typography>
              <Stack flexDirection="row" alignItems="center" mt={1}>
                <TextField
                  disabled
                  fullWidth
                  size="small"
                  required={true}
                  id="email"
                  type="email"
                  sx={{
                    "& .MuiInputBase-root": {
                      borderTopRightRadius: 0,
                      borderBottomRightRadius: 0,
                      height: "37.93px",
                    },
                    "& .MuiOutlinedInput-root": {
                      fieldset: { borderRightWidth: 0 },
                      "&.Mui-focused fieldset": {
                        borderWidth: "1px",
                        borderRightWidth: 0,
                      },
                    },
                  }}
                />
                <Button
                  disabled
                  variant="contained"
                  type="submit"
                  sx={{
                    width: "160px",
                    height: "37.93px",
                    borderTopLeftRadius: 0,
                    borderBottomLeftRadius: 0,
                    borderTopRightRadius: 8,
                    borderBottomRightRadius: 8,
                    px: 0,
                  }}
                >
                  Join email list
                </Button>
              </Stack>
            </Stack>
          </CardStyle>
        )}

        {/* Payment links */}
        {sellerProfile.payments.length > 0 && <EditPaymentLinks />}

        {/* Custom links */}
        <List disablePadding>
          <DragDropContext onDragEnd={onDragEnd}>
            <Droppable droppableId="droppable-list">
              {(provided) => (
                <Stack
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  divider={<Divider orientation="horizontal" flexItem />}
                >
                  {localLinks.map((link, index) => (
                    <UserLink
                      key={link.id}
                      id={link.id}
                      title={link.title}
                      url={link.url}
                      index={index}
                    />
                  ))}
                  {provided.placeholder}
                </Stack>
              )}
            </Droppable>
          </DragDropContext>
          <Box sx={{ width: "100%", textAlign: "center" }}>
            <AddExternalLink />
          </Box>
        </List>
      </Stack>
    </ThemeProvider>
  );
};
